import React, { useEffect, useState } from 'react';
import { useTenantBranding } from '../contexts/TenantBrandingContext';
import api from '../lib/api';
import { AlertTriangle, Snowflake, Phone, CalendarDays } from 'lucide-react';

const FreezeScreen = ({ status, frozenReason }: { status: string; frozenReason?: string | null }) => {
  const branding = useTenantBranding();
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
  const isExpired = status === 'expired';

  const [plans, setPlans] = useState<any[]>([]);
  const [support, setSupport] = useState({ whatsapp: '', display_name: '' });
  const [companyName, setCompanyName] = useState('');

  useEffect(() => {
    api.get('/api/tenant/subscription').then(r => {
      setPlans(r.data.plans || []);
      setSupport(r.data.support || { whatsapp: '', display_name: '' });
      setCompanyName(r.data.license?.company_name || '');
    }).catch(() => {});
  }, []);

  const buildWhatsAppUrl = (planName?: string) => {
    if (!support.whatsapp) return '#';
    const num = support.whatsapp.replace(/[^0-9]/g, '');
    const msg = planName
      ? `Merhaba, ${companyName || branding.display_name} icin "${planName}" paketi ile aboneligimizi yenilemek istiyoruz.`
      : `Merhaba, ${companyName || branding.display_name} icin aboneligimizi yenilemek istiyoruz.`;
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4" data-testid="freeze-screen">
      <div className="w-full max-w-2xl text-center">
        {/* Branding */}
        <div className="mb-8">
          {branding.loading ? (
            <div className="h-16 mb-4" />
          ) : branding.logo_url ? (
            <img src={`${BACKEND}${branding.logo_url}`} alt={branding.display_name || ''} className="h-16 mx-auto mb-4 object-contain" />
          ) : (
            <h1 className="text-2xl font-bold text-slate-800" data-testid="freeze-brand-name">
              {branding.display_name || 'Stüdyo'}
            </h1>
          )}
        </div>

        {/* Status card */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 mb-6">
          <div className={`w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center ${isExpired ? 'bg-amber-100' : 'bg-cyan-100'}`}>
            {isExpired ? <AlertTriangle size={32} className="text-amber-600" /> : <Snowflake size={32} className="text-cyan-600" />}
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2" data-testid="freeze-title">
            {isExpired ? 'Abonelik Suresi Doldu' : 'Hesap Donduruldu'}
          </h2>
          <p className="text-slate-500 mb-4">
            {isExpired
              ? 'Aboneliginizin suresi dolmustur. Hizmete devam edebilmek icin lutfen aboneliginizi yenileyin.'
              : 'Hesabiniz yonetici tarafindan dondurulmustur.'}
          </p>
          {frozenReason && (
            <div className="bg-slate-50 rounded-lg p-3 mb-4">
              <p className="text-sm text-slate-600"><span className="font-medium">Neden:</span> {frozenReason}</p>
            </div>
          )}
        </div>

        {/* Plans */}
        {plans.length > 0 && (
          <div className="mb-6">
            <h3 className="font-semibold text-slate-700 mb-3">Abonelik Paketleri</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {plans.map((plan: any) => (
                <div key={plan.id} className="bg-white rounded-xl shadow p-4 border border-slate-200 text-left">
                  <h4 className="font-bold text-slate-800 text-sm">{plan.name}</h4>
                  <p className="text-xs text-slate-500 mb-2">
                    <CalendarDays size={12} className="inline mr-1" />{plan.duration_days} gun
                  </p>
                  <p className="text-lg font-bold text-[#6b0f1a] mb-2">{plan.price.toLocaleString('tr-TR')} {plan.currency}</p>
                  {support.whatsapp && (
                    <a href={buildWhatsAppUrl(plan.name)} target="_blank" rel="noopener noreferrer"
                      className="block w-full text-center py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-medium transition-colors">
                      <Phone size={12} className="inline mr-1" /> Bu Paketi Sec
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Support contact */}
        {support.whatsapp ? (
          <a href={buildWhatsAppUrl()} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-colors"
            data-testid="freeze-whatsapp-button">
            <Phone size={18} />
            {support.display_name || 'WhatsApp ile Iletisim'}
          </a>
        ) : (
          <p className="text-sm text-slate-400">Destek bilgisi icin yoneticinizle iletisime gecin.</p>
        )}

        <p className="text-xs text-slate-400 mt-6">
          Abonelik yenilendikten sonra otomatik olarak erisim saglanacaktir.
        </p>
      </div>
    </div>
  );
};

export default FreezeScreen;
